import { useId, type ReactElement, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { color, layout, space } from "@tokens/index";
import { Badge, Surface, Text, useViewport } from "@primitives/index";

/**
 * AdminSection: one flat region of an admin page.
 *
 * A `Surface` at elevation 1 draws a hairline border and no shadow. The
 * heading is an `h2`; the section is labelled by it for assistive tech.
 */

interface AdminSectionProps {
    readonly title?: string | undefined;
    readonly icon?: LucideIcon | undefined;
    readonly count?: number | undefined;
    readonly description?: ReactNode;
    /** Controls that sit at the end of the heading row. */
    readonly trailing?: ReactNode;
    readonly children: ReactNode;
    readonly testId?: string | undefined;
}

export function AdminSection({
    title,
    icon: Icon,
    count,
    description,
    trailing,
    children,
    testId,
}: AdminSectionProps): ReactElement {
    const { isCompact } = useViewport();
    const titleId = useId();
    const hasHeader = title !== undefined || description !== undefined || trailing !== undefined;

    return (
        <Surface
            as="section"
            elevation={1}
            borderRadius="lg"
            padding={isCompact ? 4 : 5}
            aria-labelledby={title !== undefined ? titleId : undefined}
            data-testid={testId}
            style={{ width: "100%", minWidth: 0 }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: space[4], minWidth: 0 }}>
                {hasHeader && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: space[3],
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gap: space[1],
                                minWidth: 0,
                                flex: "1 1 200px",
                            }}
                        >
                            {title !== undefined && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: space[2],
                                        minWidth: 0,
                                    }}
                                >
                                    {Icon !== undefined && (
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                display: "inline-flex",
                                                color: color.fg.muted,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon size={16} strokeWidth={2} />
                                        </span>
                                    )}
                                    <Text as="h2" id={titleId} role="subheading" truncate>
                                        {title}
                                    </Text>
                                    {count !== undefined && (
                                        <Badge variant="neutral" size="sm" caps={false}>
                                            {count}
                                        </Badge>
                                    )}
                                </div>
                            )}
                            {description !== undefined && (
                                <Text
                                    as="p"
                                    role="caption"
                                    tone="muted"
                                    style={{ maxWidth: layout.proseMaxWidth }}
                                >
                                    {description}
                                </Text>
                            )}
                        </div>
                        {trailing !== undefined && (
                            <div style={{ display: "flex", gap: space[2], flexShrink: 0 }}>
                                {trailing}
                            </div>
                        )}
                    </div>
                )}
                {children}
            </div>
        </Surface>
    );
}
